/**
 * Goods-receipt list — `/purchasing/grns`. Cursor DataTable over
 * `GET /api/v1/grns` with the contract's supplier / PO / warehouse / date
 * filters and its allowlisted sort tokens.
 */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ColumnDef } from "@tanstack/react-table";
import {
  Button,
  Combobox,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@erp/ui";

import { DataTable, dateTimeCell, textCell } from "@/components/data-table";
import type { SortState } from "@/components/data-table";

import type { GrnSummary } from "../api";
import { useGrnList, type GrnFilters } from "../queries";
import { useSupplierNames, useWarehouseNames } from "../components/lookup-names";

const ALL = "__all__";

export interface GrnListPageProps {
  onOpenGrn: (grnId: string) => void;
  onOpenPurchaseOrder?: (purchaseOrderId: string) => void;
  canReadSuppliers?: boolean;
  canReadWarehouses?: boolean;
  /** `?purchase_order_id=` deep link from a purchase order. */
  initialPurchaseOrderId?: string;
  pageLimit?: number;
}

export function GrnListPage({
  onOpenGrn,
  onOpenPurchaseOrder,
  canReadSuppliers = true,
  canReadWarehouses = true,
  initialPurchaseOrderId,
  pageLimit,
}: GrnListPageProps) {
  const { t } = useTranslation();
  const [supplierId, setSupplierId] = useState<string | null>(null);
  const [warehouseId, setWarehouseId] = useState<string>(ALL);
  const [receivedFrom, setReceivedFrom] = useState("");
  const [receivedTo, setReceivedTo] = useState("");
  const [purchaseOrderId, setPurchaseOrderId] = useState(initialPurchaseOrderId ?? "");
  const [sort, setSort] = useState<SortState>(null);

  useEffect(() => {
    if (initialPurchaseOrderId !== undefined) setPurchaseOrderId(initialPurchaseOrderId);
  }, [initialPurchaseOrderId]);

  const { supplierName, parties } = useSupplierNames(canReadSuppliers);
  const { warehouseName, warehouses } = useWarehouseNames(canReadWarehouses);
  const supplierOptions = useMemo(
    () => parties.map((party) => ({ value: party.id, label: party.display_name })),
    [parties],
  );

  const filters = useMemo<GrnFilters>(() => {
    const built: GrnFilters = {};
    if (supplierId !== null && supplierId !== "") built.supplier_id = supplierId;
    if (warehouseId !== ALL) built.warehouse_id = warehouseId;
    if (purchaseOrderId !== "") built.purchase_order_id = purchaseOrderId;
    // The wire filter is a date-time; a date input contributes day boundaries.
    if (receivedFrom !== "") built.received_from = `${receivedFrom}T00:00:00Z`;
    if (receivedTo !== "") built.received_to = `${receivedTo}T23:59:59Z`;
    return built;
  }, [supplierId, warehouseId, purchaseOrderId, receivedFrom, receivedTo]);

  const hasFilters = Object.keys(filters).length > 0;
  const query = useGrnList(filters, sort, pageLimit);

  const columns = useMemo<ColumnDef<GrnSummary>[]>(
    () => [
      {
        accessorKey: "grn_number",
        id: "grn_number",
        header: t("purchasing.grnList.columns.number", "GRN #"),
        cell: (ctx) => (
          <span dir="ltr" className="font-mono font-medium">
            {ctx.getValue<string>()}
          </span>
        ),
      },
      {
        accessorKey: "purchase_order_id",
        id: "purchase_order",
        header: t("purchasing.grnList.columns.purchaseOrder", "Purchase order"),
        enableSorting: false,
        cell: (ctx) => {
          const id = ctx.getValue<string>();
          if (onOpenPurchaseOrder === undefined) return textCell(id);
          return (
            <Button
              type="button"
              variant="link"
              size="sm"
              className="h-auto p-0 font-mono text-xs"
              onClick={(event) => {
                event.stopPropagation();
                onOpenPurchaseOrder(id);
              }}
            >
              {t("purchasing.grnList.openPo", "Open order")}
            </Button>
          );
        },
      },
      {
        accessorKey: "warehouse_id",
        id: "warehouse",
        header: t("purchasing.grnList.columns.warehouse", "Warehouse"),
        enableSorting: false,
        cell: (ctx) => textCell(warehouseName(ctx.getValue<string>())),
      },
      {
        accessorKey: "received_at",
        id: "received_at",
        header: t("purchasing.grnList.columns.receivedAt", "Received"),
        cell: (ctx) => dateTimeCell(ctx.getValue<string>()),
      },
      {
        accessorKey: "supplier_delivery_reference_token",
        id: "delivery_reference",
        header: t("purchasing.grnList.columns.deliveryReference", "Delivery note"),
        enableSorting: false,
        cell: (ctx) => textCell(ctx.getValue<string | undefined>()),
      },
      {
        accessorKey: "created_at",
        id: "created_at",
        header: t("purchasing.grnList.columns.created", "Created"),
        cell: (ctx) => dateTimeCell(ctx.getValue<string>()),
      },
    ],
    [t, warehouseName, onOpenPurchaseOrder],
  );

  return (
    <div className="flex flex-col gap-4 p-4">
      <div>
        <h1 className="text-xl font-semibold">{t("purchasing.grnList.title", "Goods receipts")}</h1>
        <p className="text-sm text-muted-foreground">
          {t("purchasing.grnList.subtitle", "Every posted receipt against a purchase order.")}
        </p>
        {supplierId !== null && supplierId !== "" ? (
          <p className="text-xs text-muted-foreground">{supplierName(supplierId)}</p>
        ) : null}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {canReadSuppliers ? (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="purchasing-grn-supplier">
              {t("purchasing.grnList.filters.supplier", "Supplier")}
            </Label>
            <Combobox
              id="purchasing-grn-supplier"
              options={supplierOptions}
              value={supplierId}
              onChange={setSupplierId}
              placeholder={t("purchasing.common.anySupplier", "Any supplier")}
              searchPlaceholder={t("purchasing.common.searchSupplier", "Search supplier…")}
              emptyMessage={t("purchasing.common.noSuppliers", "No suppliers found.")}
            />
          </div>
        ) : null}
        {canReadWarehouses ? (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="purchasing-grn-warehouse">
              {t("purchasing.grnList.filters.warehouse", "Warehouse")}
            </Label>
            <Select value={warehouseId} onValueChange={setWarehouseId}>
              <SelectTrigger id="purchasing-grn-warehouse">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>{t("purchasing.common.all", "All")}</SelectItem>
                {warehouses.map((warehouse) => (
                  <SelectItem key={warehouse.id} value={warehouse.id}>
                    {warehouse.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : null}
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="purchasing-grn-from">
            {t("purchasing.grnList.filters.from", "Received from")}
          </Label>
          <Input
            id="purchasing-grn-from"
            type="date"
            dir="ltr"
            value={receivedFrom}
            onChange={(event) => setReceivedFrom(event.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="purchasing-grn-to">
            {t("purchasing.grnList.filters.to", "Received to")}
          </Label>
          <Input
            id="purchasing-grn-to"
            type="date"
            dir="ltr"
            value={receivedTo}
            onChange={(event) => setReceivedTo(event.target.value)}
          />
        </div>
      </div>

      {hasFilters ? (
        <div>
          <Button
            type="button"
            variant="link"
            size="sm"
            className="h-auto p-0 text-xs"
            onClick={() => {
              setSupplierId(null);
              setWarehouseId(ALL);
              setReceivedFrom("");
              setReceivedTo("");
              setPurchaseOrderId("");
            }}
          >
            {t("purchasing.common.clearFilters", "Clear filters")}
          </Button>
        </div>
      ) : null}

      <DataTable
        columns={columns}
        query={query}
        getRowId={(row) => row.id}
        sort={sort}
        onSortChange={setSort}
        onRowClick={(row) => onOpenGrn(row.id)}
        storageKey="purchasing.grns.list.columns"
        emptyMessage={
          hasFilters
            ? t("purchasing.grnList.emptyFiltered", "No goods receipts match these filters.")
            : t("purchasing.grnList.empty", "No goods receipts yet.")
        }
        errorMessage={t("purchasing.grnList.loadError", "Goods receipts could not be loaded.")}
      />
    </div>
  );
}
